import dynamic from 'next/dynamic'
import React, { FC, memo, useContext, useMemo } from 'react'
import { ScreenContext } from 'react-components'

import CancellationPolicyFilter from './filter-item/cancellation-policy-filter'
import CheckboxFilterItem from './filter-item/checkbox-filter-item'
import { FilterContentContainer, StyledFilterContent } from './styles'
import { FilterHeadPosition, FilterType } from '../types'
import { FilterSectionTransport } from '../../../../transports/search-result-listing-static-transport'
import { FilterItemTransport, FilterTransport } from '../../../../transports/filters-configuration-transport'
import PillFilterItem from './filter-item/pill-filter-item'
import { PillFilterContainer } from './content-styles'
import { preventEvent } from '../utils'
import RadioFilter from './filter-item/radio-filter'
import StepperFilter from './filter-item/stepper-filter'

const DynamicPriceFilterNoSSR = dynamic(() => import('./price-filter'), { ssr: false })

const _getCheckBoxFilter = (filter: FilterTransport) => {
    return filter.items.map((filterItem: FilterItemTransport) => {
        return (
            <CheckboxFilterItem
                filterItem={filterItem}
                filterType={filter.type}
                key={filterItem.title}
                isMultiSelect={filter?.multiple}
            />
        )
    })
}

const _getMobilePillFilter = (filter: FilterTransport) => {
    return (
        <PillFilterContainer>
            {filter.items.map((filterItem: FilterItemTransport) => {
                return <PillFilterItem filterItem={filterItem} filterType={filter.type} key={filterItem.title} />
            })}
        </PillFilterContainer>
    )
}

type Props = {
    filter: FilterTransport
    filterHeadPosition: FilterHeadPosition
    filterStaticData: FilterSectionTransport
    filterType: FilterType
    isFilterSelected: boolean
}

const FilterContent: FC<Props> = ({ filter, filterHeadPosition, filterStaticData, filterType, isFilterSelected }) => {
    const { isMobile, isTablet } = useContext(ScreenContext)

    const filterItemsList = useMemo(() => {
        if (!(isMobile || isTablet)) {
            if (filterType === FilterType.PRICES) {
                return <DynamicPriceFilterNoSSR filter={filter} key={filter.title} />
            } else if (filterType === FilterType.CANCELLATION_POLICY) {
                return (
                    <CancellationPolicyFilter
                        filterHeadPosition={filterHeadPosition}
                        filterItems={filter.items}
                        key={filter.title}
                        specificFilterStaticData={filterStaticData.specificFilters.cancellationPolicy}
                    />
                )
            } else {
                return filter.multiple ? _getCheckBoxFilter(filter) : <RadioFilter filter={filter} />
            }
        } else {
            switch (filterType) {
                case FilterType.BEDROOMS: {
                    return (
                        <StepperFilter filterItems={filter.items} key={filter.title} label={filterStaticData.bedroom} />
                    )
                }
                case FilterType.BATHROOMS: {
                    return (
                        <StepperFilter
                            filterItems={filter.items}
                            key={filter.title}
                            filterType={FilterType.BATHROOMS}
                            label={filterStaticData.bathroom}
                        />
                    )
                }
                case FilterType.STARS: {
                    return _getMobilePillFilter(filter)
                }
                case FilterType.PRICES: {
                    return <DynamicPriceFilterNoSSR filter={filter} key={filter.title} />
                }
                case FilterType.CANCELLATION_POLICY: {
                    return (
                        <CancellationPolicyFilter
                            filterItems={filter.items}
                            key={filter.title}
                            filterHeadPosition={filterHeadPosition}
                            specificFilterStaticData={filterStaticData.specificFilters.cancellationPolicy}
                        />
                    )
                }
                default: {
                    return _getCheckBoxFilter(filter)
                }
            }
        }
    }, [filter, filterHeadPosition, filterStaticData, filterType, isMobile, isTablet])

    return (
        <FilterContentContainer
            className={'filter-content'}
            isMultiple={filter.multiple}
            isPriceFilter={filterType === FilterType.PRICES}
            filterHeadPosition={filterHeadPosition}
            onClick={preventEvent}
        >
            <StyledFilterContent filterHeadPosition={filterHeadPosition}>{filterItemsList}</StyledFilterContent>
        </FilterContentContainer>
    )
}

export default memo(FilterContent)
